import { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, Button, Spinner, Alert, Badge } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import { PlusIcon, PencilIcon, TrashIcon, UserIcon, BuildingOfficeIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { useUsers, useDeleteUser, useReactivateUser } from '../hooks/useUsers';
import { CreateUserModal } from './CreateUserModal';
import { EditUserModal } from './EditUserModal';
import type { User } from '../types/user.types';
import Swal from 'sweetalert2';

const roleLabels: Record<string, string> = {
  'VENDEDOR': 'Vendedor',
  'CUSTOMER B2B': 'Cliente B2B',
  'COMPANY ADMIN': 'Administrador de Empresa',
  'ADMINISTRATOR': 'Administrador',
  'admin': 'Administrador',
  'accountant': 'Contador',
  'sales': 'Ventas',
  'user': 'Usuario',
};

export function UserManagerPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Only search if there are at least 3 characters or if it's empty (to show all)
  const searchQuery = debouncedSearch.length >= 3 || debouncedSearch.length === 0 ? debouncedSearch : '';

  // Shorter debounce (300ms) since we're doing client-side filtering (much faster)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Fetch ALL users once (cached by React Query)
  const { data: allUsersData, isLoading, error } = useUsers();
  const deleteMutation = useDeleteUser();
  const reactivateMutation = useReactivateUser();

  // Client-side filtering and pagination using useMemo for performance
  const { users: displayedUsers, total } = useMemo(() => {
    if (!allUsersData?.users) {
      return { users: [], total: 0 };
    }

    const allUsers = allUsersData.users;

    // Filter users based on search query
    let filteredUsers = allUsers;
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filteredUsers = allUsers.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        const idNumber = (user.idNumber || '').toLowerCase();

        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          idNumber.includes(searchLower)
        );
      });
    }

    // Paginate filtered results
    const pageSize = 10;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    console.log('📊 Client-side filtering:', {
      allUsers: allUsers.length,
      filtered: filteredUsers.length,
      paginated: paginatedUsers.length,
      search: searchQuery,
      page: currentPage,
    });

    return {
      users: paginatedUsers,
      total: filteredUsers.length,
    };
  }, [allUsersData, searchQuery, currentPage]);

  // Create data object compatible with existing code
  const data = useMemo(() => {
    if (!allUsersData) return undefined;

    return {
      users: displayedUsers,
      total,
      page: currentPage,
      pageSize: 10,
      hasMorePages: currentPage * 10 < total,
    };
  }, [displayedUsers, total, currentPage, allUsersData]);

  const handleOpenCreate = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleDelete = async (user: User) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se desactivará al usuario "${user.firstName} ${user.lastName}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync(user.id);
        Swal.fire({
          title: 'Usuario desactivado',
          text: 'El usuario ha sido desactivado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
      } catch (error: any) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo desactivar el usuario',
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      }
    }
  };

  const handleReactivate = async (user: User) => {
    const result = await Swal.fire({
      title: '¿Reactivar usuario?',
      text: `El usuario "${user.firstName} ${user.lastName}" volverá a estar activo.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await reactivateMutation.mutateAsync(user.id);
        Swal.fire({
          title: 'Usuario reactivado',
          text: 'El usuario ha sido reactivado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
      } catch (error: any) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo reactivar el usuario',
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar usuarios">
        Ocurrió un error al cargar los usuarios. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Gestión de Usuarios</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Administra los usuarios del sistema
          </p>
        </div>
        <Button
          data-guide="Nuevo Usuario"
          className="w-full sm:w-auto"
          leftIcon={<PlusIcon className="w-5 h-5" />}
          onClick={handleOpenCreate}
        >
          Nuevo Usuario
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody className="p-4">
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Buscar usuarios (mínimo 3 caracteres)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-md rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {search.length > 0 && search.length < 3 && (
              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                Escribe al menos 3 caracteres para buscar
              </p>
            )}
            {search !== debouncedSearch && search.length >= 3 && (
              <p className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1">
                <Spinner size="sm" />
                Buscando...
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {data?.users.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                No hay usuarios
              </h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                {searchQuery ? `No se encontraron resultados para "${searchQuery}"` : 'Comienza creando tu primer usuario'}
              </p>
              {searchQuery && (
                <p className="mt-2 text-xs text-secondary-400 dark:text-secondary-500">
                  Intenta buscar con menos caracteres o verifica el nombre exacto
                </p>
              )}
              {!searchQuery && (
                <div className="mt-6">
                  <Button onClick={handleOpenCreate} disabled>
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Nuevo Usuario
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.users.map((user, index) => (
                  <TableRow key={user.id || `user-${index}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={`${user.firstName} ${user.lastName}`}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                            <UserIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-secondary-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </div>
                          {user.id && (
                            <div className="text-xs text-secondary-500 dark:text-secondary-400">
                              ID: {user.id.substring(0, 8)}...
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-secondary-600 dark:text-secondary-400">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-secondary-600 dark:text-secondary-400">
                      {user.phone || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="w-4 h-4 text-secondary-400" />
                        <div>
                          <div className="text-sm font-medium text-secondary-900 dark:text-white">
                            {user.companyName}
                          </div>
                          {user.companyCode && (
                            <div className="text-xs text-secondary-500 dark:text-secondary-400">
                              {user.companyCode}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role, idx) => (
                            <Badge key={idx} variant="default" size="sm">
                              {roleLabels[role] || role}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="default" size="sm">
                            {roleLabels[user.role] || user.role}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.isActive ? (
                        <Badge variant="success" size="sm">
                          Activo
                        </Badge>
                      ) : (
                        <Badge variant="danger" size="sm">
                          Inactivo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {user.isActive && (
                          <button
                            onClick={() => handleOpenEdit(user)}
                            className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Editar usuario"
                            disabled={deleteMutation.isPending}
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                        )}
                        {user.isActive && (
                          <button
                            onClick={() => handleDelete(user)}
                            className="text-danger-600 hover:text-danger-900 dark:hover:text-danger-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Desactivar usuario"
                            disabled={deleteMutation.isPending}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                        {!user.isActive && (
                          <button
                            onClick={() => handleReactivate(user)}
                            className="text-success-600 hover:text-success-700 dark:hover:text-success-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Reactivar usuario"
                            disabled={reactivateMutation.isPending}
                          >
                            <ArrowUturnLeftIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Paginación */}
      {data && data.users.length > 0 && (
        <Card>
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                  Mostrando {(currentPage - 1) * data.pageSize + 1} a{' '}
                  {Math.min(currentPage * data.pageSize, data.total)} de {data.total} usuarios
                </p>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">
                  Página {currentPage} de {Math.ceil(data.total / data.pageSize)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!data.hasMorePages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modal Create User */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
      />

      {/* Modal Edit User */}
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        user={selectedUser}
      />
    </div>
  );
}
